import { useState } from "react";
import { Calendar, FileDown, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";

function formatReportDate(date: Date): string {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

interface AnalyticsHeaderProps {
  onExport: () => Promise<void>
  exportDisabled?: boolean
}

export function AnalyticsHeader({ onExport, exportDisabled = false }: AnalyticsHeaderProps): JSX.Element {
  const reportDate = formatReportDate(new Date())
  const [isExporting, setIsExporting] = useState(false)

  async function handleExport(): Promise<void> {
    if (!window.confirm('Export the analytics report as a PDF?')) return
    setIsExporting(true)
    try {
      await onExport()
      toast.success('Analytics report exported')
    } catch {
      toast.error('Failed to export report')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 pb-2 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
          Analytics & Resource Optimization
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Data-driven insights for better decisions
        </p>
        <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
          <Calendar className="h-4 w-4" />
          <span>{reportDate}</span>
        </div>
      </div>
      <button
        onClick={handleExport}
        disabled={isExporting || exportDisabled}
        data-html2canvas-ignore="true"
        className="flex w-fit shrink-0 items-center gap-2 rounded-lg bg-brand-green px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
        {isExporting ? 'Exporting...' : 'Export Report'}
      </button>
    </div>
  );
}
